import { valibotResolver } from "@hookform/resolvers/valibot";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";

import PermissionsSelection from "common/components/PermissionsSelection";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { USER_PERMISSIONS_CATEGORIES } from "common/constants";
import useMembers from "common/datahooks/useMembers";
import { HTTPError } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { typedObjectKeys } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { EmailSchema } from "common/schemas";
import type { UserPermissions } from "common/types";

import useMembersMutations from "../../pages/workspace-settings/datahooks/useMembersMutations";

export default function InviteMemberModal({
  buttonComponent,
}: {
  buttonComponent: ReactNode;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { members } = useMembers();

  const {
    inviteMember,
    isInvitingMember,
    inviteMemberError,
    resetInviteMember,
  } = useMembersMutations();

  const { max_members: maxMembers } = useSelectedWorkspaceContext();

  const {
    register,
    reset: resetForm,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<{
    email: string;
  }>({
    resolver: valibotResolver(EmailSchema),
  });
  const [selectedEmail, setSelectedEmail] = useState<string>(null);
  const [permissions, setPermissions] = useState<UserPermissions>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  function resetStates() {
    resetForm();
    setSelectedEmail(null);
    resetInviteMember();
    setPermissions([]);
  }

  function onNext({ email }: { email: string }) {
    const existingMember = members.find((member) => member.email === email);
    if (existingMember) {
      setError("email", {
        message: "User is already a member of this workspace",
      });
    } else {
      setSelectedEmail(email);
    }
  }

  function onSubmit() {
    const isAdmin = typedObjectKeys(USER_PERMISSIONS_CATEGORIES).every((key) =>
      permissions.includes(`${key}_full`),
    );
    inviteMember({
      email: selectedEmail,
      permissions,
      role: isAdmin ? "admin" : "member",
    }).then(() => {
      setIsDialogOpen(false);
      resetStates();
    });
  }

  let errorMessage: string;

  if (inviteMemberError) {
    if (
      inviteMemberError instanceof HTTPError &&
      inviteMemberError.type === "UserIsAlreadyMember"
    ) {
      errorMessage = "User is already a member of this workspace";
    } else {
      errorMessage = "Error inviting member, please try again later";
    }
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  const hasMaxMembers = members
    ? maxMembers && members.length >= maxMembers
    : true;

  return (
    <RestrictedComponent visibleForPermissions={["members_full"]}>
      <Component
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onAfterClose={resetStates}
      >
        <Trigger disabled={hasMaxMembers} asChild>
          {buttonComponent}
        </Trigger>

        <Content>
          <Title>
            {selectedEmail
              ? `Set permissions for ${selectedEmail}`
              : "Invite a member"}
          </Title>

          <Description className={selectedEmail ? "mb-4" : "mb-8"}>
            {selectedEmail
              ? "Give this member full access to the workspace, or limit which pages they can manage"
              : "Invite someone to this workspace. They will get an email invite  with a link to join you"}
          </Description>

          <form
            onSubmit={handleSubmit(selectedEmail ? onSubmit : onNext)}
            className="flex w-full flex-col"
          >
            {selectedEmail ? (
              <>
                <PermissionsSelection
                  permissions={permissions}
                  setPermissions={setPermissions}
                />
                {errorMessage && (
                  <p className="mx-3 mt-3 text-sm text-red-500">
                    {errorMessage}
                  </p>
                )}
              </>
            ) : (
              <FormField
                htmlFor="email"
                label="Email"
                size="lg"
                error={errorMessage || errors.email?.message}
              >
                <Input
                  {...register("email", { onChange: resetInviteMember })}
                  hasError={!!errorMessage || !!errors.email?.message}
                  placeholder="Teammate email"
                  variant="lg"
                  id="email"
                />
              </FormField>
            )}

            <div className="mt-8 gap-x-2.5 flex justify-between">
              <Close asChild>
                <Button
                  variant="secondary-black"
                  type="button"
                  disabled={isInvitingMember}
                >
                  Cancel
                </Button>
              </Close>

              <Button type="submit" isLoading={isInvitingMember}>
                {selectedEmail ? "Invite" : "Next"}
              </Button>
            </div>
          </form>
        </Content>
      </Component>
    </RestrictedComponent>
  );
}
